import { useCallback, useEffect, useState } from 'react';
import { dayIn } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { money } from '../lib/format';
import type { DriverRow, ZoneRow } from '../lib/types';

/** 一组指标 —— 总览和每个司机都是这一组(金额都是「分」的字符串) */
interface Metrics {
  orders: number;
  amount: string;
  pendingOrders: number;
  noneOrders: number;
  noneAmount: string;
  cash: string;
  transfer: string;
  unknown: string;
  outstandingOrders: number;
  outstanding: string;
  handed: string;
  confirmed: string;
}

interface Stats {
  currency: string | null;
  mixedCurrency: boolean;
  summary: Metrics | null;
  byDriver: Array<Metrics & { driverId: string | null; driverName: string | null; driverNo: string | null }>;
  byDay: Array<{ day: string; orders: number; amount: string }>;
}

const METHODS = [
  ['', '全部方式'],
  ['CASH', '现金'],
  ['TRANSFER', '转账'],
  ['NONE', '标记没收到'],
  ['UNKNOWN', '没记方式(旧单)'],
] as const;
const STATES = [
  ['', '现金全部状态'],
  ['OUTSTANDING', '还在司机手上'],
  ['HANDED', '已交,待确认'],
  ['CONFIRMED', '已确认收到'],
] as const;

const INPUT =
  'h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm dark:border-slate-600 dark:bg-slate-800';

/**
 * 司机代收款统计。
 *
 * ★ 回答财务每天要问的三件事:这段时间司机一共代收了多少;
 *   其中现金有多少还压在司机手上、多少交了还没点确认;按司机看各是多少。
 *
 * ★ 按**送达时间**算(钱是送达那一刻收的),日期按店里的时区。
 * ★ 金额待定的单不算进金额(还不知道是多少),单独数出来,提醒去补金额。
 */
export function CodStats({ drivers, zones, timezone }: { drivers: DriverRow[]; zones: ZoneRow[]; timezone: string }) {
  const daysAgo = (n: number) => dayIn(timezone, new Date(Date.now() - n * 24 * 3600_000));
  const [from, setFrom] = useState(() => daysAgo(29));
  const [to, setTo] = useState(() => daysAgo(0));
  const [driverId, setDriverId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [method, setMethod] = useState('');
  const [state, setState] = useState('');
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    type C = { id: string; name: string };
    api<C[] | { data: C[] }>('/customers')
      .then((r) => setCustomers(Array.isArray(r) ? r : r.data))
      .catch(() => setCustomers([]));
  }, []);

  const load = useCallback(async () => {
    const qs = new URLSearchParams({ from, to });
    if (driverId) qs.set('driverId', driverId);
    if (customerId) qs.set('customerId', customerId);
    if (zoneId) qs.set('zoneId', zoneId);
    if (method) qs.set('method', method);
    if (state) qs.set('state', state);
    try {
      setStats(await api<Stats>(`/settlements/cod-stats?${qs.toString()}`));
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, [from, to, driverId, customerId, zoneId, method, state]);

  useEffect(() => {
    void load();
  }, [load]);

  const cur = stats?.currency ?? '';
  const m = (v: string) => money(v, cur);
  const s = stats?.summary;
  const filtered = !!(driverId || customerId || zoneId || method || state);

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">代收款</h1>
        <p className="mt-1 text-sm text-slate-500">按送达时间统计司机代收的货款(店里的时区)</p>
      </div>

      <div className="flex flex-wrap items-end gap-2" aria-label="筛选">
        <label className="text-xs text-slate-500">
          从
          <input type="date" aria-label="开始日期" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className={`${INPUT} ml-1`} />
        </label>
        <label className="text-xs text-slate-500">
          到
          <input type="date" aria-label="结束日期" value={to} min={from} onChange={(e) => setTo(e.target.value)} className={`${INPUT} ml-1`} />
        </label>
        {(
          [
            ['今天', 0],
            ['近 7 天', 6],
            ['近 30 天', 29],
          ] as const
        ).map(([label, n]) => (
          <button
            key={label}
            type="button"
            onClick={() => {
              setFrom(daysAgo(n));
              setTo(daysAgo(0));
            }}
            className="h-9 rounded-lg border border-slate-300 px-3 text-sm text-slate-600 dark:border-slate-600 dark:text-slate-300"
          >
            {label}
          </button>
        ))}
        <select aria-label="司机" value={driverId} onChange={(e) => setDriverId(e.target.value)} className={INPUT}>
          <option value="">全部司机</option>
          {drivers.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}({d.driverNo})
            </option>
          ))}
        </select>
        <select aria-label="商家" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={INPUT}>
          <option value="">全部商家</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select aria-label="区域" value={zoneId} onChange={(e) => setZoneId(e.target.value)} className={INPUT}>
          <option value="">全部区域</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.code} {z.name}
            </option>
          ))}
        </select>
        <select aria-label="收款方式" value={method} onChange={(e) => setMethod(e.target.value)} className={INPUT}>
          {METHODS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select aria-label="现金状态" value={state} onChange={(e) => setState(e.target.value)} className={INPUT}>
          {STATES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        {filtered && (
          <button
            type="button"
            onClick={() => {
              setDriverId('');
              setCustomerId('');
              setZoneId('');
              setMethod('');
              setState('');
            }}
            className="h-9 px-2 text-sm text-brand-600"
          >
            清除筛选
          </button>
        )}
      </div>

      {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
      {stats?.mixedCurrency && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          这段时间的单有不止一种币种,下面的合计把它们直接加在一起了 —— 按商家筛选后再看。
        </p>
      )}

      {s && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="代收总览">
            <Card label="已收合计" value={m(s.amount)} note={`${s.orders} 单`} strong />
            <Card label="现金" value={m(s.cash)} />
            <Card label="转账" value={m(s.transfer)} />
            <Card
              label="还在司机手上的现金"
              value={m(s.outstanding)}
              note={`${s.outstandingOrders} 单没交`}
              warn={s.outstandingOrders > 0}
            />
            <Card label="已交,待确认" value={m(s.handed)} />
            <Card label="已确认收到" value={m(s.confirmed)} />
            {s.pendingOrders > 0 && (
              <Card label="金额待定" value={`${s.pendingOrders} 单`} note="还不知道收了多少,不在合计里 —— 去订单里补金额" warn />
            )}
            {s.noneOrders > 0 && <Card label="标记没收到" value={m(s.noneAmount)} note={`${s.noneOrders} 单,不在合计里`} />}
            {s.unknown !== '0' && <Card label="没记方式(旧单)" value={m(s.unknown)} />}
          </div>

          <Section title="按司机">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="p-2">司机</th>
                  {['单数', '已收合计', '现金', '转账', '手上未交', '已交待确认', '已确认', '金额待定'].map((h) => (
                    <th key={h} className="p-2 text-right">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.byDriver.length === 0 && (
                  <tr>
                    <td colSpan={9} className="p-4 text-center text-slate-400">
                      这段时间没有代收的单
                    </td>
                  </tr>
                )}
                {stats.byDriver.map((d) => (
                  <tr key={d.driverId ?? 'none'} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="p-2 text-slate-900 dark:text-slate-100">
                      {d.driverName ? `${d.driverName}(${d.driverNo})` : '(没有司机)'}
                    </td>
                    <Num v={String(d.orders)} />
                    <Num v={m(d.amount)} strong />
                    <Num v={m(d.cash)} />
                    <Num v={m(d.transfer)} />
                    <Num v={m(d.outstanding)} warn={d.outstanding !== '0'} />
                    <Num v={m(d.handed)} />
                    <Num v={m(d.confirmed)} />
                    <Num v={d.pendingOrders > 0 ? `${d.pendingOrders} 单` : '—'} />
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="按天">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="p-2">日期</th>
                  <th className="p-2 text-right">单数</th>
                  <th className="p-2 text-right">已收合计</th>
                </tr>
              </thead>
              <tbody>
                {stats.byDay.map((d) => (
                  <tr key={d.day} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="p-2 tabular-nums text-slate-900 dark:text-slate-100">{d.day}</td>
                    <Num v={String(d.orders)} />
                    <Num v={m(d.amount)} />
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}

function Card({ label, value, note, strong, warn }: { label: string; value: string; note?: string; strong?: boolean; warn?: boolean }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
      <div className="text-xs text-slate-500">{label}</div>
      <div
        className={`mt-1 tabular-nums ${strong ? 'text-xl font-semibold' : 'text-lg'} ${
          warn ? 'text-amber-600 dark:text-amber-400' : 'text-slate-900 dark:text-slate-100'
        }`}
      >
        {value}
      </div>
      {note && <div className="mt-0.5 text-xs text-slate-400">{note}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className="rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <h2 className="border-b border-slate-200 px-3 py-2 text-sm font-medium text-slate-900 dark:border-slate-700 dark:text-slate-100">
        {title}
      </h2>
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}

function Num({ v, strong, warn }: { v: string; strong?: boolean; warn?: boolean }) {
  return (
    <td
      className={`p-2 text-right tabular-nums ${strong ? 'font-medium text-slate-900 dark:text-slate-100' : ''} ${
        warn ? 'text-amber-600 dark:text-amber-400' : ''
      }`}
    >
      {v}
    </td>
  );
}
